import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FullPageLoader } from "@/components/ui/full-page-loader";

interface InvoiceOrderItem {
  _id: string;
  productSnapshot: {
    name: string;
    description?: string;
  };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface InvoiceOrder {
  _id: string;
  orderNumber: string;
  createdAt: string;
  business?: {
    name?: string | null;
    logo?: string | null;
  } | null;
  store?: {
    name?: string | null;
    logo?: string | null;
    whatsappNumber?: string | null;
    url?: string | null;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    } | null;
  } | null;
  customer: {
    name: string;
    email: string;
    phone: string;
    address?: string;
  };
  items: InvoiceOrderItem[];
  pricing: {
    subtotal: number;
    shipping: number;
    tax: number;
    discount: number;
    total: number;
    currency: string;
  };
  shipping: {
    method: string;
  };
  payment: {
    method?: string;
    status?: string;
    amount?: number;
    currency?: string;
  };
}

export function InvoicePage() {
  const { orderId, token } = useParams<{
    orderId: string;
    token: string;
  }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<InvoiceOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!orderId || !token) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await api.get(
          `/api/orders/${orderId}/invoice/${token}`
        );

        if (response.data?.success && response.data?.order) {
          setOrder(response.data.order);
        } else {
          setError("Invoice not found");
        }
      } catch (err: unknown) {
        console.error("Error fetching invoice:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load invoice details"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [orderId, token]);

  const formatPrice = (amount: number, currency: string) => {
    return amount.toLocaleString("en-NG", {
      style: "currency",
      currency: currency || "NGN",
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return <FullPageLoader text="Loading invoice" variant="page" />;
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive">Invoice Not Found</CardTitle>
            <CardDescription>
              We couldn't locate the invoice you're looking for.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error || "Invoice not found"}</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/")}>Return to Homepage</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto">
        {/* Print button */}
        <div className="flex justify-end mb-4 print:hidden">
          <Button onClick={() => window.print()} size="sm">
            Download / Print
          </Button>
        </div>

        <Card className="border border-gray-200 print:border-none">
          {/* Invoice Header */}
          <CardHeader className="p-6 pb-4 border-b border-gray-100">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-3">
                {(order.store?.logo || order.business?.logo) && (
                  <img
                    src={order.store?.logo || order.business?.logo || ""}
                    alt={order.store?.name || order.business?.name || "Business logo"}
                    className="h-12 w-12 rounded-lg object-cover border border-gray-100"
                  />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    {order.store?.name || order.business?.name}
                  </p>
                  {order.store?.address && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {[order.store.address.street, order.store.address.city, order.store.address.state]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                </div>
              </div>
              <div className="sm:text-right">
                <p className="text-2xl font-bold text-gray-900 tracking-tight">INVOICE</p>
                <p className="text-sm text-muted-foreground mt-0.5">#{order.orderNumber}</p>
                <p className="text-xs text-muted-foreground">{formatDate(order.createdAt)}</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Billed To / Invoice Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Billed To</p>
                <p className="font-medium text-gray-900">{order.customer.name}</p>
                {order.customer.address && <p className="text-muted-foreground">{order.customer.address}</p>}
                <p className="text-muted-foreground">{order.customer.email}</p>
                <p className="text-muted-foreground">{order.customer.phone}</p>
              </div>
              <div className="space-y-1 sm:text-right">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Invoice Details</p>
                <p className="text-muted-foreground">Date: <span className="text-gray-900 font-medium">{formatDate(order.createdAt)}</span></p>
                <p className="text-muted-foreground">Order: <span className="text-gray-900 font-medium">#{order.orderNumber}</span></p>
                <p className="text-muted-foreground capitalize">Payment: <span className="text-gray-900 font-medium">{order.payment.method}</span></p>
              </div>
            </div>

            {/* Items Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Item</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={item._id} className={idx > 0 ? "border-t border-gray-100" : ""}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{item.productSnapshot?.name}</p>
                        {item.productSnapshot?.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{item.productSnapshot.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {formatPrice(item.unitPrice, order.pricing.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatPrice(item.totalPrice, order.pricing.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-full max-w-xs space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="text-foreground">{formatPrice(order.pricing.subtotal, order.pricing.currency)}</span>
                </div>
                {order.pricing.shipping > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Shipping</span>
                    <span className="text-foreground">{formatPrice(order.pricing.shipping, order.pricing.currency)}</span>
                  </div>
                )}
                {order.pricing.tax > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax</span>
                    <span className="text-foreground">{formatPrice(order.pricing.tax, order.pricing.currency)}</span>
                  </div>
                )}
                {order.pricing.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>−{formatPrice(order.pricing.discount, order.pricing.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>{formatPrice(order.pricing.total, order.pricing.currency)}</span>
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="px-6 pb-6 pt-0 print:hidden">
            <Button className="w-full" onClick={() => window.print()}>
              Download / Print
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}


